import React, { useContext, useEffect, useState } from 'react';
import {
  Button,
  Column,
  FooterBlock,
  HeaderBlock,
  Link,
  Loader,
  LocaleContext,
  Message,
  Query,
  Row,
  Section,
} from 'shared';

import { adaptApiLink, getApiResourceUrl } from '../../utils';

import { getReportDisplayName, ReportFileEntry, sortReportFiles } from './reportFiles';
import { Report, ReportIcon, ReportList, ReportName, ReportPageSection, ReportPageText } from './ReportPage.styles';
import { ReportPageProps } from './ReportPage.types';

type DisplayReport = {
  id: string;
  name: string;
  displayName: string;
  url: string;
  mtime: string;
};

export const ReportPage = ({ onFetchReportPage }: ReportPageProps) => {
  const localeContext = useContext(LocaleContext);
  const [storageReports, setStorageReports] = useState<DisplayReport[] | null>(null);
  const [isStorageReportsLoading, setIsStorageReportsLoading] = useState(true);
  const [storageReportsError, setStorageReportsError] = useState(false);

  useEffect(() => {
    let isRequestActive = true;

    if (!window.fetch) {
      setStorageReportsError(true);
      setIsStorageReportsLoading(false);

      return undefined;
    }

    window
      .fetch('/reports/list/', { cache: 'no-store' })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Unable to load report files: ${response.status}`);
        }

        return response.json();
      })
      .then((entries: ReportFileEntry[]) => {
        if (!Array.isArray(entries)) {
          throw new Error('Invalid report directory listing');
        }

        const reports = entries
          .filter(entry => entry && entry.type === 'file' && /\.pdf$/i.test(entry.name))
          .map(entry => ({
            displayName: getReportDisplayName(entry.name),
            id: entry.name,
            mtime: entry.mtime,
            name: entry.name,
            url: `/reports/files/${encodeURIComponent(entry.name)}`,
          }));

        if (isRequestActive) {
          setStorageReports(sortReportFiles(reports));
        }
      })
      .catch(error => {
        console.error(error);
        if (isRequestActive) {
          setStorageReportsError(true);
        }
      })
      .then(() => {
        if (isRequestActive) {
          setIsStorageReportsLoading(false);
        }
      });

    return () => {
      isRequestActive = false;
    };
  }, []);

  return (
    <LocaleContext.Consumer>
      {({ formatMessage }) => (
        <Query onFetch={onFetchReportPage}>
          {({ error, loading, response }) => (
            <Row flexFlow="column" height="100%">
              {response &&
                response.payload.banners &&
                response.payload.banners.map(
                  banner =>
                    banner.type === 'HERO' && (
                      <Column mx={[1, 2]} mt={[1, 1, 0]} mb={[1, 0]} key={banner.id} flexBasis="auto">
                        <HeaderBlock
                          description={banner.content}
                          image={banner.image ? getApiResourceUrl(banner.image) : ''}
                          link={banner.link ? adaptApiLink(banner.link) : undefined}
                          title={banner.title}
                          overlayColor={banner.overlayColor}
                        />
                      </Column>
                    ),
                )}

              <Column flexBasis="auto">
                {loading && <Loader />}

                {error && (
                  <Message title={formatMessage({ id: 'LIST.ERROR.TITLE' })}>
                    {formatMessage({ id: 'LIST.ERROR.CONTENT' })}
                  </Message>
                )}

                {!loading && response && (
                  <ReportPageSection className="report__page__section">
                    {response.payload.text && (
                      <ReportPageText>
                        {response.payload.text.split('\\n').map((item, i) => (
                          <p key={i}>{item}</p>
                        ))}
                      </ReportPageText>
                    )}
                    {storageReportsError && (
                      <Message title={formatMessage({ id: 'REPORT.LIST.ERROR.TITLE' })}>
                        {formatMessage({ id: 'REPORT.LIST.ERROR.CONTENT' })}
                      </Message>
                    )}
                    {isStorageReportsLoading && <Loader />}
                    {!isStorageReportsLoading &&
                      (() => {
                        const reports = storageReports || [];

                        return reports.length ? (
                          <ReportList>
                            {reports.map(report => (
                              <Report className="report" key={report.id}>
                                <ReportIcon className="report__icon" />
                                <ReportName className="report__name">{report.displayName}</ReportName>
                                <Button
                                  as={Link}
                                  {...{
                                    external: true,
                                    target: '_blank',
                                    url: report.url,
                                  }}
                                  type="leading"
                                  className="button__download"
                                >
                                  {localeContext.formatMessage({ id: 'REPORT.DOWNLOAD' })}
                                </Button>
                              </Report>
                            ))}
                          </ReportList>
                        ) : storageReportsError ? null : (
                          <Message title={formatMessage({ id: 'LIST.EMPTY.TITLE' })}>
                            {formatMessage({ id: 'LIST.EMPTY.CONTENT' })}
                          </Message>
                        );
                      })()}
                  </ReportPageSection>
                )}
              </Column>
              {response &&
                response.payload.banners &&
                response.payload.banners.map(
                  banner =>
                    banner.type === 'FOOTER' && (
                      <Column mx={[1, 2, 2, 6]} mt={[1, '2rem']} mb={[0, '2rem']} key={banner.id}>
                        <Section>
                          <FooterBlock
                            description={banner.content}
                            image={banner.image ? getApiResourceUrl(banner.image) : ''}
                            link={banner.link ? adaptApiLink(banner.link) : undefined}
                            title={banner.title}
                          />
                        </Section>
                      </Column>
                    ),
                )}
            </Row>
          )}
        </Query>
      )}
    </LocaleContext.Consumer>
  );
};
